import * as React from "react"

import { useFeatureFlag } from "@common/api/feature-flags"
import { DictationButton } from "@common/features/recording/dictation-button"
import { DICTATION } from "@common/flags"
import { cn } from "@common/lib/utils"

function insertAtCursor(textarea: HTMLTextAreaElement, text: string) {
  const { value } = textarea
  const editing = document.activeElement === textarea
  const start = editing ? textarea.selectionStart : value.length
  const end = editing ? textarea.selectionEnd : value.length
  const before = value.slice(0, start)
  const after = value.slice(end)
  const separator = before && !/\s$/.test(before) ? " " : ""
  const trailing = after && !/^\s/.test(after) ? " " : ""
  const inserted = `${before}${separator}${text}`
  const setValue = Object.getOwnPropertyDescriptor(
    HTMLTextAreaElement.prototype,
    "value"
  )?.set
  setValue?.call(textarea, `${inserted}${trailing}${after}`)
  textarea.dispatchEvent(new Event("input", { bubbles: true }))
  const caret = inserted.length
  textarea.focus()
  textarea.setSelectionRange(caret, caret)
}

const Textarea = React.forwardRef<
  HTMLTextAreaElement,
  React.ComponentProps<"textarea"> & { dictation?: boolean }
>(({ className, dictation = false, ...props }, ref) => {
  const enabled = useFeatureFlag(DICTATION) === true
  const editable = !props.disabled && !props.readOnly
  const inner = React.useRef<HTMLTextAreaElement | null>(null)
  const dictationRef = React.useCallback(
    (node: HTMLTextAreaElement | null) => {
      inner.current = node
      if (typeof ref === "function") {
        ref(node)
      } else if (ref) {
        ref.current = node
      }
    },
    [ref]
  )
  const textarea = (
    <textarea
      className={cn(
        "flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-body-medium ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
        className,
        dictation && enabled && "pr-9"
      )}
      ref={dictation ? dictationRef : ref}
      {...props}
    />
  )
  return dictation ? (
    <div className="relative">
      {textarea}
      {enabled && editable && (
        <div className="absolute right-1 bottom-1">
          <DictationButton
            onTranscribed={(text) =>
              inner.current && insertAtCursor(inner.current, text)
            }
          />
        </div>
      )}
    </div>
  ) : (
    textarea
  )
})
Textarea.displayName = "Textarea"

export { Textarea }
